/**
 * @file Radio / RadioGroup 单选
 * @description 自定义单选外观；组内互斥。
 *
 * @whenToUse 互斥选项（性别、状态枚举等）。
 * @whenNotToUse 多选（Checkbox）；布尔开关（Switch）。
 */

import {
  createContext,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react'
import { cn } from '@/lib/cn.ts'

type RadioGroupContextValue = {
  name: string
  value?: string
  onChange?: (value: string) => void
  disabled?: boolean
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null)

export type RadioGroupProps = {
  /** 单选组 name；不传则自动生成 */
  name?: string
  /** 受控值 */
  value?: string
  /** 值变化 */
  onChange?: (value: string) => void
  disabled?: boolean
  className?: string
  children: ReactNode
}

export function RadioGroup({
  name,
  value,
  onChange,
  disabled,
  className,
  children,
}: RadioGroupProps) {
  const autoName = useId()
  return (
    <RadioGroupContext.Provider
      value={{ name: name ?? autoName, value, onChange, disabled }}
    >
      <div role="radiogroup" className={cn('flex flex-col gap-2', className)}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  )
}

export type RadioProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size' | 'onChange'
> & {
  /** option 值 */
  value: string
  /** 文案 */
  label?: ReactNode
  onChange?: (value: string) => void
}

export function Radio({
  value,
  label,
  className,
  disabled,
  id,
  name,
  onChange,
  ...rest
}: RadioProps) {
  const group = useContext(RadioGroupContext)
  const inputName = name ?? group?.name
  const isDisabled = disabled || group?.disabled
  const checked =
    group?.value !== undefined ? group.value === value : rest.checked

  return (
    <label
      htmlFor={id}
      className={cn(
        'inline-flex cursor-pointer items-center gap-2 text-sm text-foreground transition-opacity duration-200',
        isDisabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <span className="relative inline-flex size-4 shrink-0">
        <input
          id={id}
          type="radio"
          name={inputName}
          value={value}
          disabled={isDisabled}
          checked={checked}
          className="peer absolute inset-0 z-10 cursor-pointer opacity-0 disabled:cursor-not-allowed"
          {...rest}
          onChange={() => {
            group?.onChange?.(value)
            onChange?.(value)
          }}
        />
        <span
          className="pointer-events-none flex size-4 items-center justify-center rounded-full border-2 border-border bg-raised transition-opacity duration-200 peer-checked:border-primary peer-focus-visible:opacity-90 peer-checked:[&>span]:opacity-100"
          aria-hidden
        >
          <span className="size-2 rounded-full bg-primary opacity-0 transition-opacity duration-200" />
        </span>
      </span>
      {label ? <span>{label}</span> : null}
    </label>
  )
}
